import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../services/api';

const AUTH_KEY = 'chesi_customer_token';
const CUSTOMER_KEY = 'chesi_customer';

const AuthContext = createContext(null);

function loadStoredAuth() {
  try {
    const token = localStorage.getItem(AUTH_KEY);
    const customer = localStorage.getItem(CUSTOMER_KEY);
    return {
      token: token || null,
      customer: customer ? JSON.parse(customer) : null
    };
  } catch {
    return { token: null, customer: null };
  }
}

export function AuthProvider({ children }) {
  const stored = loadStoredAuth();
  const [token, setToken] = useState(stored.token);
  const [customer, setCustomer] = useState(stored.customer);
  const [loading, setLoading] = useState(Boolean(stored.token));

  const persist = useCallback((newToken, newCustomer) => {
    setToken(newToken);
    setCustomer(newCustomer);
    if (newToken) {
      localStorage.setItem(AUTH_KEY, newToken);
      localStorage.setItem(CUSTOMER_KEY, JSON.stringify(newCustomer));
      api.setAuthToken(newToken);
    } else {
      localStorage.removeItem(AUTH_KEY);
      localStorage.removeItem(CUSTOMER_KEY);
      api.setAuthToken(null);
    }
  }, []);

  useEffect(() => {
    if (token) api.setAuthToken(token);
  }, [token]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getCustomerMe();
        if (!cancelled) {
          setCustomer(res.data);
          localStorage.setItem(CUSTOMER_KEY, JSON.stringify(res.data));
        }
      } catch {
        if (!cancelled) persist(null, null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, persist]);

  const login = async (email, password, rememberMe = false) => {
    const res = await api.customerLogin({ email, password, remember_me: rememberMe });
    persist(res.data.token, res.data.customer);
    return res.data.customer;
  };

  const register = async (data) => {
    const res = await api.customerRegister(data);
    persist(res.data.token, res.data.customer);
    return res.data.customer;
  };

  const logout = () => persist(null, null);

  const updateCustomer = (updated) => {
    setCustomer(updated);
    localStorage.setItem(CUSTOMER_KEY, JSON.stringify(updated));
  };

  const value = {
    token,
    customer,
    loading,
    isAuthenticated: Boolean(token && customer),
    login,
    register,
    logout,
    updateCustomer
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useRequireAuth() {
  const auth = useAuth();
  return auth;
}
